<script setup lang="ts">
import { computed } from 'vue';

import { brand } from '#/config/brand';
import { cn } from '#/lib/utils';

/**
 * 브랜드 로고(대입로1st) 단일 이미지.
 *
 * 자산 선택은 `brand.logos` 2축을 그대로 따른다 — 어떤 파일을 쓸지는 여기서 정하지 않고
 * prop 조합으로 고르기만 한다(파일 경로 SSoT 는 `config/brand.ts`).
 *
 *  - `size`: `wordmark`(기본) 워드마크 단독 / `full` 태그라인 포함.
 *    태그라인은 높이 60px 미만에서 읽히지 않으므로, 헤더·셸처럼 작은 자리는 `wordmark` 를 쓴다.
 *  - `variant`: `default` 밝은 배경 / `invert` 다크 배경.
 *    `invert` 는 CSS 필터가 아니라 **별도 라이트 자산**을 쓴다 — 필터로 흰색을 만들면
 *    브랜드 그린 그라디언트가 통째로 사라진다(brand.ts logos 주석 참조).
 */
interface Props {
  /** invert: 어두운 배경(푸터 등)용 라이트 자산 */
  variant?: 'default' | 'invert';
  /** full: 태그라인 포함 — 60px 이상 크게 놓는 자리에서만 */
  size?: 'wordmark' | 'full';
  /**
   * 표시 높이. 자산 선택(`size`)과 분리한 별도 축 — 같은 자산을 자리마다 다른 크기로 쓴다.
   *  - `shell`(기본): 헤더·셸 기준(36 → md 48px)
   *  - `auth`: 인증 화면(BlankLayout) — 셸이 없어 로고가 유일한 브랜드 앵커라 크게(h-logo-auth=150px)
   */
  height?: 'shell' | 'auth';
}
const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  size: 'wordmark',
  height: 'shell',
});

const logo = computed(() => {
  const { wordmark, wordmarkLight, full, fullLight } = brand.logos;
  const invert = props.variant === 'invert';
  if (props.size === 'full') return invert ? fullLight : full;
  return invert ? wordmarkLight : wordmark;
});

// mobile-first. shell = 헤더 기준(36 → md 48px = 3rem, 헤더 74px 안에서 상하 여백 확보).
// auth = 인증 화면 전용(100 → md 150px). 모바일은 뷰포트 폭에 눌리므로 비례 축소한다.
const imgClass = computed(() =>
  cn(
    'block w-auto flex-none',
    props.height === 'auth' ? 'h-logo-auth-sm md:h-logo-auth' : 'h-9 md:h-12',
  ),
);
</script>

<template>
  <img :class="imgClass" :src="logo.src" :alt="logo.alt" />
</template>
